<template>
  <div class="ae">
    <!-- Carte interrupteur principal -->
    <div
      class="ae-toggle"
      :class="{ 'ae-toggle--on': isActive }"
    >
      <span class="ae-toggle__icon">
        <i class="fa-regular fa-user-slash" />
      </span>
      <span class="ae-toggle__text">
        <span class="ae-toggle__title">{{ t('billing.autoExclusion.toggleTitle') }}</span>
        <span class="ae-toggle__desc">{{ t('billing.autoExclusion.toggleDesc') }}</span>
      </span>
      <button
        type="button"
        class="ae-switch"
        :class="isActive ? 'ae-switch--on' : 'ae-switch--off'"
        :aria-pressed="isActive"
        @click="toggleActive"
      >
        <span class="ae-switch__track" />
        <span class="ae-switch__label">
          {{ isActive ? t('billing.autoExclusion.enabled') : t('billing.autoExclusion.disabled') }}
        </span>
      </button>
    </div>

    <div
      v-if="loading"
      class="ae-loading"
    >
      <ProgressSpinner style="width: 30px; height: 30px" />
    </div>

    <!-- Section segment (visible uniquement si l'exclusion est active) -->
    <template v-else-if="isActive">
      <div class="ae-section">
        <span class="ae-section__label">{{ t('billing.autoExclusion.segmentLabel') }}</span>

        <!-- Replié : statut + action d'édition + compteur -->
        <div
          v-if="!editing"
          class="ae-segment"
        >
          <div class="ae-segment__row">
            <span
              class="ae-segment__status"
              :class="{ 'ae-segment__status--ok': hasSegment }"
            >
              <i :class="hasSegment ? 'fa-regular fa-circle-check' : 'fa-regular fa-circle-dashed'" />
              {{ hasSegment
                ? t('billing.autoExclusion.segmentDefined')
                : t('billing.autoExclusion.noSegment') }}
            </span>
            <SpmButton
              :label="hasSegment
                ? t('billing.autoExclusion.edit')
                : t('billing.autoExclusion.configure')"
              :icon="hasSegment ? 'pi pi-pencil' : 'pi pi-plus'"
              class-style="p-button p-button-outlined p-button-sm"
              @click="startEditing"
            />
          </div>
          <div
            v-if="hasSegment"
            class="ae-segment__count"
          >
            <i class="fa-regular fa-circle-info" />
            {{ t('billing.autoExclusion.currentlyExcluded', { count: excludedCount }) }}
          </div>
        </div>

        <!-- Ouvert : builder de segment -->
        <div
          v-else
          class="ae-builder"
        >
          <SegmentationsPanel
            v-model:model-value="segmentsConfig"
            origin="list"
            @count-updated="onCountUpdated"
          />
          <p class="ae-builder__preview">
            <i
              v-if="previewLoading"
              class="pi pi-spin pi-spinner"
            />
            <span v-else-if="previewCount !== null">
              {{ t('billing.autoExclusion.preview', { count: previewCount }) }}
            </span>
          </p>
        </div>
      </div>

      <p class="ae-note">
        <i class="fa-regular fa-circle-info" />
        {{ t('billing.autoExclusion.manualNote') }}
      </p>
    </template>

    <p
      v-else
      class="ae-disabled-note"
    >
      {{ t('billing.autoExclusion.disabledNote') }}
    </p>

    <div class="ae-footer">
      <SpmButton
        :label="t('common.cancel')"
        class-style="p-button p-button-text"
        @click="$emit('close')"
      />
      <SpmButton
        :label="t('billing.autoExclusion.save')"
        icon="pi pi-check"
        class-style="p-button p-button-success"
        :loading="saving"
        @click="save"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import ProgressSpinner from 'primevue/progressspinner';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import {
  fetchBillingAutoExclusion,
  setBillingAutoExclusion,
} from '@/composables/billing/billingApi';
import { showToastError, showToastSuccess } from '@/helpers';

/**
 * Contenu de la modal « Exclure automatiquement des contacts de la facturation »
 * (offre V5), ouverte depuis le hero de la page abonnement. Réutilise le builder
 * de segment (SegmentationsPanel) SANS shops_lists ; la config est persistée côté
 * payments (`/billing/auto-exclusion`), recalculée SYNCHRONEMENT à l'enregistrement.
 *
 * Le builder est REPLIÉ par défaut (statut + bouton) ; « Modifier » l'ouvre,
 * « Enregistrer » persiste, recalcule, rafraîchit l'overview et ferme la modal.
 */
export default defineComponent({
  name: 'BillingAutoExclusionPanel',

  components: {
    ProgressSpinner,
    SpmButton,
    SegmentationsPanel,
  },

  emits: ['saved', 'close'],

  setup(_props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const loading = ref(true);
    const saving = ref(false);
    const editing = ref(false);
    const isActive = ref(false);
    // Config par défaut = un bloc vide (même convention que l'éditeur de liste).
    const segmentsConfig = ref<any>([[{}]]);
    const excludedCount = ref(0);
    const previewCount = ref<number | null>(null);
    const previewLoading = ref(false);

    // Un segment est « défini » dès qu'au moins une condition est renseignée.
    const hasSegment = computed(
      () => Array.isArray(segmentsConfig.value)
        && segmentsConfig.value.some(
          (block: any) => Array.isArray(block)
            && block.some((c: any) => c && Object.keys(c).length > 0),
        ),
    );

    const onCountUpdated = (payload: { count: number | null; loading: boolean }) => {
      previewLoading.value = payload.loading;
      if (!payload.loading) previewCount.value = payload.count;
    };

    const toggleActive = () => {
      isActive.value = !isActive.value;
      // À l'activation sans segment encore défini, on ouvre directement le builder.
      if (isActive.value && !hasSegment.value) editing.value = true;
      if (!isActive.value) editing.value = false;
    };

    const startEditing = () => {
      editing.value = true;
    };

    const reloadConfig = async () => {
      const cfg = await fetchBillingAutoExclusion();
      if (cfg) {
        isActive.value = cfg.isActive;
        if (Array.isArray(cfg.segmentsConfig) && cfg.segmentsConfig.length > 0) {
          segmentsConfig.value = cfg.segmentsConfig;
        }
        excludedCount.value = cfg.excludedCount ?? 0;
      }
    };

    const save = async () => {
      // Règle active mais aucun segment défini → on ouvre le builder.
      if (isActive.value && !hasSegment.value) {
        editing.value = true;
        showToastError(t('billing.autoExclusion.defineSegmentFirst'));
        return;
      }
      // Valide le segment via le store de l'éditeur (panel monté en mode édition).
      if (isActive.value && editing.value) {
        await store.dispatch('segmentationsEditor/validate');
        if (store.getters['segmentationsEditor/getHasError']) {
          showToastError(t('billing.autoExclusion.invalidSegment'));
          return;
        }
      }

      saving.value = true;
      try {
        const res = await setBillingAutoExclusion({
          isActive: isActive.value,
          segmentsConfig: segmentsConfig.value,
        });
        if (!res) {
          showToastError(t('billing.autoExclusion.saveError'));
          return;
        }
        // Le back recalcule SYNCHRONEMENT et renvoie le nombre d'exclus à jour.
        excludedCount.value = res.excludedCount ?? excludedCount.value;
        editing.value = false;
        showToastSuccess(t('billing.autoExclusion.saved'));
        // Rafraîchit l'overview de la page (carte « contacts actifs / non facturables »).
        await store.dispatch('billing/refetchOverview');
        // Ferme la modal (le parent écoute `saved`).
        emit('saved');
      } finally {
        saving.value = false;
      }
    };

    onMounted(async () => {
      await reloadConfig();
      loading.value = false;
    });

    return {
      t,
      loading,
      saving,
      editing,
      isActive,
      segmentsConfig,
      excludedCount,
      previewCount,
      previewLoading,
      hasSegment,
      onCountUpdated,
      toggleActive,
      startEditing,
      save,
    };
  },
});
</script>

<style lang="scss" scoped>
.ae {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-top: 4px;

  // ── Carte interrupteur ───────────────────────────────────────
  &-toggle {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    border: 1px solid #e7ebf0;
    border-radius: 14px;
    background: #fafbfc;
    transition: border-color 0.2s ease, background 0.2s ease;

    &--on {
      border-color: #cfe3a8;
      background: #f6fbef;
    }

    &__icon {
      flex: none;
      width: 42px;
      height: 42px;
      border-radius: 11px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1rem;
      color: #64748b;
      background: #eef2f7;
    }

    &--on &__icon {
      color: #5a8a1f;
      background: #e9f4d6;
    }

    &__text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
      min-width: 0;
    }

    &__title {
      font-weight: 700;
      font-size: 0.95rem;
      color: #1f2937;
    }

    &__desc {
      font-size: 0.8125rem;
      color: #64748b;
      line-height: 1.45;
    }
  }

  &-loading {
    text-align: center;
    padding: 20px 0;
  }

  // ── Section segment ──────────────────────────────────────────
  &-section {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &__label {
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #94a3b8;
    }
  }

  &-segment {
    border: 1px solid #e7ebf0;
    border-radius: 12px;
    padding: 14px 16px;
    background: #fff;

    &__row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
    }

    &__status {
      display: inline-flex;
      align-items: center;
      gap: 9px;
      font-size: 0.875rem;
      font-weight: 600;
      color: #475569;

      i {
        font-size: 1rem;
        color: #cbd5e1;
      }

      &--ok i {
        color: #94c840;
      }
    }

    &__count {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      margin-top: 12px;
      padding: 7px 11px;
      border-radius: 8px;
      background: #f1f5f9;
      color: #475569;
      font-size: 0.8125rem;
      font-weight: 500;

      i {
        color: #94a3b8;
      }
    }
  }

  &-builder {
    border: 1px solid #e7ebf0;
    border-radius: 12px;
    padding: 14px;
    background: #fcfdfe;

    &__preview {
      font-size: 0.8125rem;
      color: #475569;
      font-weight: 600;
      margin: 12px 0 0;
      min-height: 1em;
    }
  }

  &-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0;
    font-size: 0.8125rem;
    color: #94a3b8;
    line-height: 1.45;

    i {
      margin-top: 2px;
    }
  }

  &-disabled-note {
    margin: 0;
    font-size: 0.875rem;
    color: #94a3b8;
    font-style: italic;
  }

  &-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 4px;
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
  }
}

// ── Interrupteur (switch) ──────────────────────────────────────
.ae-switch {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 0;

  &__track {
    width: 42px;
    height: 24px;
    border-radius: 999px;
    position: relative;
    transition: background 0.2s ease;

    &::after {
      content: "";
      position: absolute;
      top: 3px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #fff;
      transition: left 0.2s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    }
  }

  &--on {
    color: #5a8a1f;

    .ae-switch__track {
      background: #94c840;

      &::after {
        left: 21px;
      }
    }
  }

  &--off {
    color: #94a3b8;

    .ae-switch__track {
      background: #cbd5e1;

      &::after {
        left: 3px;
      }
    }
  }
}
</style>
